<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('product.products') }}
        </span>
        <h3 class="page-title">
          {{ $t('product.tag.editingTagCategory') }}
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/produkty/tagi')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button
          theme="success"
          @click.prevent="validateForm">
          <i class="material-icons">add</i>
          {{ $t('product.tag.editTagCategory') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <validation-observer ref="category-edit-form" tag="form">
        <d-card class="mb-5">
          <d-tabs
            v-if="!loadError"
            v-sticky-tabs
            card >
            <!-- Dane ogólne -->
            <d-tab
              :title="$t('ui.generalInfo')"
              active>
              <h4 class="mb-3 pb-3 border-bottom">
                {{ $t('ui.generalInfo') }}
              </h4>

              <div class="form-group">
                <label>
                  {{ $t('product.tag.categoryName') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="name" />

                  <d-form-invalid-feedback>
                    {{ errors[0] }}
                  </d-form-invalid-feedback>
                </validation-provider>
              </div>

              <div class="form-group">
                <d-checkbox
                  inline
                  toggle-small
                  v-model="is_main"
                  toggle>
                  {{ $t('product.tag.isMain') }}
                </d-checkbox>
              </div>
            </d-tab>

            <d-tab :title="$t('ui.tags')">
              <h4 class="pb-3 mb-0 border-bottom d-flex">
                {{ $t('ui.tags') }}

                <d-input-group class="ml-auto new-form-input">
                  <d-input
                    v-model="newTagName"
                    class="mr-2" />

                  <d-button
                    class="ml-auto"
                    theme="success"
                    @click.prevent="addTag">
                    <i class="material-icons">add</i>
                    {{ $t('product.tag.addTag') }}
                  </d-button>
                </d-input-group>
              </h4>

              <table class="table mt-2 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0">{{ $t('ui.value') }}</th>
                    <th scope="col" class="border-0" style="width: 200px;">{{ $t('ui.actions') }}</th>
                  </tr>
                </thead>
                <tbody v-if="tags.length">
                  <tr
                    v-for="(tag, index) of tags"
                    :key="'tag-' + index">
                    <td class="align-middle">
                      {{ tag.name }}
                    </td>
                    <td class="align-middle">
                      <d-button
                        theme="primary"
                        @click.prevent="initTagEdit(tag.id, tag.name)"
                        :title="$t('ui.edit')">
                        <i class="material-icons">edit</i>
                      </d-button>

                      <d-button
                        theme="primary"
                        :outline="true"
                        @click.prevent="addTranslation('tag', tag.id)"
                        :title="$t('settings.transaltion.addTranslation')"
                        class="ml-2">
                        <i class="material-icons">language</i>
                      </d-button>

                      <d-button
                        theme="danger"
                        @click.prevent="removeTag(index)"
                        :title="$t('ui.delete')"
                        class="ml-2">
                        <i class="material-icons">delete</i>
                      </d-button>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="3" class="text-center">
                      {{ $t('product.tag.noTagsInThisCategory') }}&hellip;
                    </td>
                  </tr>
                </tbody>
              </table>
            </d-tab>

            <d-tab
              :disabled="is_main || !loadedMainCategories"
              :title="$t('product.tag.mainCategories')">
              <p class="pb-3 mb-0 d-flex">
                {{ $t('product.tag.mainCategoriesDesc', { name }) }}
              </p>

              <div
                v-for="(mainCategory, index) of mainCategories"
                :key="'main-category-' + index">
                <d-checkbox
                  inline
                  v-model="parentsState[mainCategory.id]">
                  {{ mainCategory.name }}
                </d-checkbox>
              </div>
            </d-tab>

            <d-tab :title="$t('product.tag.categoryTranslations')">
              <h4 class="pb-3 mb-0 border-bottom d-flex">
                {{ $t('product.tag.categoryTranslations') }}

                <d-button
                  class="ml-auto"
                  theme="success"
                  @click.prevent="addTranslation('category', id)">
                  <i class="material-icons">add</i>
                  {{ $t('settings.transaltion.addTranslation') }}
                </d-button>
              </h4>

              <table class="table mt-2 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.language') }}</th>
                    <th scope="col" class="border-0" style="width: calc(100% - 350px);">{{ $t('product.tag.categoryName') }}</th>
                    <th scope="col" class="border-0" style="width: 200px;">{{ $t('ui.actions') }}</th>
                  </tr>
                </thead>
                <tbody v-if="category_translations.length">
                  <tr
                    v-for="(translation, index) of category_translations"
                    :key="'category-translation-' + index">
                    <td class="align-middle">
                      {{ getLanguageByID(translation.language_id) }}
                    </td>
                    <td class="align-middle">
                      {{ translation.name }}
                    </td>
                    <td class="align-middle">
                      <d-button
                        theme="primary"
                        @click.prevent="editTranslation('category', translation)"
                        class="mr-2"
                        :title="$t('ui.edit')">
                        <i class="material-icons">edit</i>
                      </d-button>
                      <d-button
                        theme="danger"
                        :disabled="removeTranslationInProgress"
                        @click.prevent="removeTranslation('category', translation.id)"
                        :title="$t('ui.delete')">
                        <i class="material-icons">delete</i>
                      </d-button>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="3" class="text-center">
                      {{ $t('product.tag.noTranslationsForThisTagCategory') }}&hellip;
                    </td>
                  </tr>
                </tbody>
              </table>
            </d-tab>

            <d-tab :title="$t('product.tag.tagTranslations')">
              <h4 class="pb-3 mb-0 border-bottom d-flex">
                {{ $t('product.tag.tagTranslations') }}
              </h4>

              <table class="table mt-2 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.language') }}</th>
                    <th scope="col" class="border-0" style="width: 25%;">{{ $t('product.tag.name') }}</th>
                    <th scope="col" class="border-0">{{ $t('settings.transaltion.translation') }}</th>
                    <th scope="col" class="border-0" style="width: 200px;">{{ $t('ui.actions') }}</th>
                  </tr>
                </thead>
                <tbody v-if="tagTranslations.length">
                  <tr
                    v-for="(translation, index) of tagTranslations"
                    :key="'tag-translation-' + index">
                    <td class="align-middle">
                      {{ getLanguageByID(translation.language_id) }}
                    </td>
                    <td class="align-middle">
                      {{ translation.tag_name }}
                    </td>
                    <td class="align-middle">
                      {{ translation.name }}
                    </td>
                    <td class="align-middle">
                      <d-button
                        theme="primary"
                        @click.prevent="editTranslation('tag', translation)"
                        class="mr-2"
                        :title="$t('ui.edit')">
                        <i class="material-icons">edit</i>
                      </d-button>
                      <d-button
                        theme="danger"
                        :disabled="removeTranslationInProgress"
                        @click.prevent="removeTranslation('tag', translation.id)"
                        :title="$t('ui.delete')">
                        <i class="material-icons">delete</i>
                      </d-button>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="3" class="text-center">
                      {{ $t('product.tag.noTranslationsForThisTags') }}&hellip;
                    </td>
                  </tr>
                </tbody>
              </table>
            </d-tab>
          </d-tabs>

          <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
            {{ $t('ui.dataLoading') }}&hellip;
          </div>

          <d-alert
            v-if="loadError"
            show
            theme="warning">
            {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
          </d-alert>
        </d-card>
      </validation-observer>
    </d-form>

    <product-tag-translation-new-popup
      v-if="showAddTranslationPopup"
      :itemID="selectedItemID"
      :itemType="selectedItemType"
      :languages="languages" />

    <product-tag-translation-edit-popup
      v-if="showEditTranslationPopup"
      :itemType="selectedItemType"
      :translationData="selectedTranslation"
      :languages="languages" />
  </d-container>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';
import ProductTagTranslationNewPopup from './ProductTagTranslationNewPopup';
import ProductTagTranslationEditPopup from './ProductTagTranslationEditPopup';
import Vue from 'vue';

export default {
  name: 'category-edit',
  components: {
    'product-tag-translation-new-popup': ProductTagTranslationNewPopup,
    'product-tag-translation-edit-popup': ProductTagTranslationEditPopup
  },
  computed: {
    allDataLoaded () {
      return this.loadedData && this.loadedLanguages;
    },
    tagTranslations () {
      if (!this.tags) {
        return [];
      }

      return this.tags.flatMap(tag => tag.tag_translations);
    }
  },
  data() {
    return {
      name: '',
      category_translations: [],
      is_main: false,
      languages: [],
      tags: [],
      parents: [],
      parentsState: {},
      mainCategories: [],
      newTagName: '',
      loadedData: false,
      loadedMainCategories: false,
      loadedLanguages: false,
      loadError: false,
      selectedItemID: false,
      selectedItemType: false,
      showAddTranslationPopup: false,
      showEditTranslationPopup: false,
      selectedTranslation: 0,
      removeTranslationInProgress: false
    };
  },
  mounted () {
    this.loadData();
    this.loadAdditionalData();
    this.loadLanguages();
    this.$bus.$on('product-tag-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$on('product-tag-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$on('product-tag-reload-translations', this.loadTranslations);
  },
  methods: {
    loadData() {
      FormUtils.loadItemData(this, {
        endpointBase: '/tags/categories/item/',
        instanceFields: ['name', 'tags', 'is_main', 'parents', 'category_translations'],
        booleanFields: ['is_main'],
        loadedKey: 'loadedData',
        errorKey: 'loadError',
        params: {
          with: ['tags', 'parents', 'categoryTranslations', 'tags.tagTranslations']
        },
        successAction: () => {
          if (!this.tags) {
            this.tags = [];
          }

          for (let i = 0; i < this.parents.length; i++) {
            let parentID = this.parents[i].id;
            this.parentsState[parentID] = true;
          }
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/tags/categories/items',
        outputKey: 'mainCategories',
        errorKey: 'loadError',
        responseKey: 'data',
        loadedKey: 'loadedMainCategories',
        noPagination: true,
        params: {
          where: {
            is_main: 1
          }
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['category-edit-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = {
        name: this.name,
        is_main: this.is_main
      };

      if (!formDataToSend.is_main) {
        let parentIDs = Object.keys(this.parentsState);
        let parents = [];

        for (let i = 0; i < parentIDs.length; i++) {
          let id = parentIDs[i];
          if (this.parentsState[id]) {
            parents.push(id);
          }
        }

        formDataToSend.parents = parents;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/tags/categories/update/' + this.id,
        formData: formDataToSend,
        successTitle: this.$t('product.tag.editCategorySuccessTitle'),
        successText: this.$t('product.tag.editCategorySuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('product.tag.editCategoryErrorText'),
        successAction: () => {
          FormUtils.loadItemData(this, {
            endpointBase: '/tags/categories/item/',
            instanceFields: ['name', 'tags', 'is_main', 'parents', 'category_translations'],
            booleanFields: ['is_main'],
            loadedKey: 'loadedData',
            errorKey: 'loadError',
            params: {
              with: ['tags', 'parents', 'tags.tagTranslations', 'categoryTranslations']
            },
            successAction: () => {
              if (!this.tags) {
                this.tags = [];
              }
            }
          });
        }
      });
    },
    addTag () {
      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/tags/store',
        formData: {
          name: this.newTagName,
          category_id: this.id
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: () => {
          Vue.swal({
            title: this.$t('product.tag.tagAddedSuccessTitle'),
            html: this.$t('product.tag.tagAddedSuccessText'),
            type: 'success',
            timer: 2000,
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });

          FormUtils.loadItemData(this, {
            endpointBase: '/tags/categories/item/',
            instanceFields: ['tags'],
            loadedKey: 'loadedData',
            errorKey: 'loadError',
            params: {
              with: ['tags', 'tags.tagTranslations']
            },
            successAction: () => {
              if (!this.tags) {
                this.tags = [];
              }
            }
          });

          this.newTagName = '';
        }
      });
    },
    async initTagEdit (id, name) {
      let newName = await Vue.swal({
        title: this.$t('product.tag.enterNewTagName'),
        input: 'text',
        inputValue: name,
        showCancelButton: true,
        inputValidator: (value) => {
          if (!value) {
            return this.$t('product.tag.tagNameCannotBeEmpty')
          }

          return false;
        }
      });

      if (newName && newName.value && newName.value !== name) {
        FormUtils.submitInBackground({
          $http: this.$http,
          endpoint: '/tags/update/' + parseInt(id, 10),
          formData: {
            name: newName.value,
            category_id: this.id
          },
          errorTitle: this.$t('ui.errorOccured'),
          errorText: this.$t('ui.actionFailedTryAgain'),
          successAction: () => {
            Vue.swal({
              title: this.$t('product.tag.tagEditSuccessTitle'),
              html: this.$t('product.tag.tagEditSuccessText'),
              type: 'success',
              timer: 2000,
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });

            FormUtils.loadItemData(this, {
              endpointBase: '/tags/categories/item/',
              instanceFields: ['tags'],
              loadedKey: 'loadedData',
              errorKey: 'loadError',
              params: {
                with: ['tags']
              },
              successAction: () => {
                if (!this.tags) {
                  this.tags = [];
                }
              }
            });
          }
        });
      }
    },
    removeTag (index) {
      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/tags/delete/',
        id: this.tags[index].id,
        successTitle: this.$t('product.tag.deleteTagSuccessTitle'),
        successText: this.$t('product.tag.deleteTagSuccessText'),
        confirmTitle: this.$t('product.tag.deleteTagConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('product.tag.deleteTagErrorText'),
        successAction: () => {
          FormUtils.loadItemData(this, {
            endpointBase: '/tags/categories/item/',
            instanceFields: ['tags'],
            loadedKey: 'loadedData',
            errorKey: 'loadError',
            params: {
              with: ['tags', 'tags.tagTranslations']
            },
            successAction: () => {
              if (!this.tags) {
                this.tags = [];
              }
            }
          });
        },
        cancelAction: () => false
      });
    },
    loadLanguages () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/languages/items',
        loadedKey: 'loadedLanguages',
        outputKey: 'languages',
        errorKey: 'loadError'
      });
    },
    getLanguageByID (languageID) {
      let filteredLanguages = this.languages.filter(language => language.id === languageID);

      if (filteredLanguages.length) {
        return filteredLanguages[0].name;
      }

      return '';
    },
    loadTranslations () {
      FormUtils.loadItemData(this, {
        endpointBase: '/tags/categories/item/',
        instanceFields: ['tags', 'category_translations'],
        loadedKey: null,
        params: {
          with: ['categoryTranslations', 'tags', 'tags.tagTranslations', 'parents']
        }
      });
    },
    addTranslation (type, itemID) {
      this.selectedItemID = itemID;
      this.selectedItemType = type;
      this.showAddTranslationPopup = true;
    },
    editTranslation (type, translationData) {
      this.selectedItemType = type;
      this.selectedTranslation = JSON.parse(JSON.stringify(translationData));
      this.showEditTranslationPopup = true;
    },
    closeAddTranslationPopup () {
      this.showAddTranslationPopup = false;
    },
    closeEditTranslationPopup () {
      this.showEditTranslationPopup = false;
    },
    removeTranslation (type, translationID) {
      this.selectedItemType = type;
      this.removeTranslationInProgress = true;
      let endpointUrl = '/tags/translations/delete/';

      if (type === 'category') {
        endpointUrl = '/tags/categories/translations/delete/';
      }

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: endpointUrl,
        id: translationID,
        successTitle: this.$t('settings.transaltion.deleteTranslationSuccessTitle'),
        successText: this.$t('settings.transaltion.deleteTranslationSuccessText'),
        confirmTitle: this.$t('settings.transaltion.deleteTranslationConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.transaltion.deleteTranslationErrorText'),
        successAction: () => {
          this.removeTranslationInProgress = false;
          this.loadTranslations();
        },
        cancelAction: () => {
          this.removeTranslationInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('product-tag-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$off('product-tag-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$off('product-tag-reload-translations', this.loadTranslations);
  }
};
</script>

<style lang="scss" scoped>
.new-form-input {
  width: 300px;
}
</style>
